import React, { useEffect } from "react";
import { MusicPracticeRoomSelectDateFormStyled } from "./styled";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { RENDER_MONTH } from "utils/functions/date";
import {
  IS_PRACTICE_ROOM_CLOSED_DAY,
  PRACTICE_ROOM_CLOSED_WEEKDAYS,
} from "utils/functions/roomBooking";
import { CelendarWidget } from "widgets";
import { Buttons, Inputs } from "components";

const MusicPracticeRoomSelectDateForm = ({
  initialValues,
  dateValue,
  startDate,
  endDate,
  holidayData,
  timeSlotData,
  onChangeMonth,
  onClickPrevWeek,
  onClickNextWeek,
  isDisabledForm,
  onBack,
  onSubmit,
}) => {
  const schema = yup.object().shape({
    room_ref_id: yup.object().required("required"),
    booking_date: yup
      .string()
      .required("required")
      // ปฏิทินกดวันจันทร์ไม่ได้อยู่แล้ว กันไว้อีกชั้นเผื่อใบร่างเก่าที่เลือกวันจันทร์ไว้ก่อนปิด
      .test(
        "closed_day",
        // ไม่มีจุดท้าย: DateTime แปลด้วย t() ที่ใช้ "." เป็นตัวคั่น key
        "Music practice room is closed on Monday",
        (value) => !IS_PRACTICE_ROOM_CLOSED_DAY(value),
      ),
    // ยังไม่เลือก ค่าเป็น "" yup จะฟ้อง type error เป็นภาษาอังกฤษ ต้องใส่ typeError ด้วย
    event_start_time: yup
      .object()
      .typeError("Please select a start time")
      .required("Please select a start time"),
    event_end_time: yup
      .object()
      .typeError("Please select an end time")
      .required("Please select an end time"),
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
      event_start_time: initialValues?.event_start_time
        ? {
            value: initialValues.event_start_time,
            label: initialValues.event_start_time,
          }
        : "",
      event_end_time: initialValues?.event_end_time
        ? {
            value: initialValues.event_end_time,
            label: initialValues.event_end_time,
          }
        : "",
    },
  });

  const { i18n, t } = useTranslation(["common", "form", "room"]);

  const eventStartTime = watch("event_start_time");
  const eventEndTime = watch("event_end_time");

  // เวลาจบต้องห่างจากเวลาเริ่มอย่างน้อย 30 นาที (จองขั้นต่ำ)
  const getFilteredEndTimeOptions = (startTime) => {
    if (!startTime?.value) return MUSIC_PRACTICE_ROOM_END_TIME_OPTIONS;
    return MUSIC_PRACTICE_ROOM_END_TIME_OPTIONS.filter((option) =>
      IS_MIN_DURATION(startTime.value, option.value),
    );
  };

  // เปลี่ยนเวลาเริ่มแล้วเวลาจบเดิมสั้นกว่าขั้นต่ำ ล้างเวลาจบให้เลือกใหม่
  useEffect(() => {
    if (eventStartTime?.value && eventEndTime?.value) {
      if (!IS_MIN_DURATION(eventStartTime.value, eventEndTime.value)) {
        setValue("event_end_time", "");
      }
    }
  }, [eventStartTime, eventEndTime, setValue]);

  return (
    <MusicPracticeRoomSelectDateFormStyled>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* ห้องมาจากที่ลูกค้าเลือกในหน้ารายการห้อง และแสดงชื่อ/ราคาที่ container แล้ว จึงไม่แสดง dropdown ซ้ำ
            ถ้าจะเปลี่ยนห้องให้กดย้อนกลับไปเลือกใหม่ (กันเปลี่ยนห้องแล้วราคา/ตารางเวลาไม่ตรงกับที่เห็นมา)
            ยังต้องลงทะเบียน field ไว้ ห้ามลบ Controller นี้ เพราะ handleSubmit ใช้ room_ref_id ไปสร้างใบจอง */}
        <Controller
          control={control}
          render={() =>
            // ปกติไม่แสดงอะไร ขึ้นเฉพาะตอนหาห้องไม่เจอ ให้ลูกค้ารู้ว่าต้องย้อนกลับไปเลือกห้อง
            errors.room_ref_id ? (
              <div className="input_wrap room_error">
                {t("Select a Room", { ns: "form" })}
              </div>
            ) : null
          }
          name="room_ref_id"
          defaultValue=""
        />

        {!isDisabledForm && (
          <>
            <div className="input_wrap">
              <Inputs.DropdownSelect
                disabled={isDisabledForm}
                value={dateValue}
                placeholder={
                  i18n.language === "en"
                    ? RENDER_MONTH(moment(startDate).format("MM"))?.en
                    : RENDER_MONTH(moment(startDate).format("MM"))?.th
                }
                label={t("Check Availability Before Booking", { ns: "room" })}
                options={(() => {
                  const options = [];
                  for (let i = 0; i < 5; i++) {
                    const targetDate = moment().add(i, "months");
                    const monthValue = targetDate.format("MM");
                    const yearValue = targetDate.format("YYYY");
                    const monthLabel =
                      i18n.language === "en"
                        ? RENDER_MONTH(monthValue)?.en
                        : RENDER_MONTH(monthValue)?.th;
                    options.push({
                      label: `${monthLabel} ${yearValue}`,
                      value: `${yearValue}-${monthValue}`,
                    });
                  }
                  return options;
                })()}
                onChange={onChangeMonth}
              />
            </div>
            <div className="calendar_wrap">
              <CelendarWidget
                type="room"
                startDate={startDate}
                endDate={endDate}
                timeData={TIME_DATA}
                closedWeekdays={PRACTICE_ROOM_CLOSED_WEEKDAYS}
                timeSlotData={timeSlotData || []}
                holidayData={holidayData || []}
                onClickPrevWeek={onClickPrevWeek}
                onClickNextWeek={onClickNextWeek}
              />
            </div>
            <div className="room_rec_label">
              {t(
                "* Available times shown may not guarantee a successful booking. Please wait for admin confirmation.",
                { ns: "room" },
              )}
            </div>
          </>
        )}
        <div className="form_title">
          {t(
            "Select Date & Time Please select the date and time you wish to book",
            { ns: "form" },
          )}
        </div>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.DateTime
                {...field}
                disabled={isDisabledForm}
                // ห้ามเลือกวันย้อนหลัง (API draft/sent ก็ไม่รับ) เหมือนฟอร์ม Music Hall
                minDate={new Date()}
                // ห้องซ้อมปิดวันจันทร์ กดเลือกในปฏิทินไม่ได้
                calendarProps={{
                  tileDisabled: ({ date, view }) =>
                    view === "month" && IS_PRACTICE_ROOM_CLOSED_DAY(date),
                }}
                label={`1. ${t("Select Date", { ns: "form" })}`}
                calendarPosition="top"
                errors={errors.booking_date?.message}
              />
            )}
            name="booking_date"
            defaultValue=""
          />
        </div>
        <div className="input_wrap mb_12">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.DropdownSelect
                {...field}
                disabled={isDisabledForm}
                label={`2. ${t("Select Time Slot", { ns: "form" })}`}
                menuPlacement="top"
                options={MUSIC_PRACTICE_ROOM_START_TIME_OPTIONS}
                placeholder={t("Start Time", { ns: "common" })}
                errors={errors.event_start_time?.message}
              />
            )}
            name="event_start_time"
            defaultValue=""
          />
        </div>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.DropdownSelect
                {...field}
                disabled={isDisabledForm}
                menuPlacement="top"
                options={getFilteredEndTimeOptions(eventStartTime)}
                placeholder={t("End Time", { ns: "common" })}
                errors={errors.event_end_time?.message}
              />
            )}
            name="event_end_time"
            defaultValue=""
          />
          <div className="time_rec_label">
            * {t("Minimum Booking 30 Minutes", { ns: "form" })}
          </div>
        </div>
        {/* หมายเหตุของห้องซ้อม เก็บใน special_request (field เดียวกับ Special Request ของ Music Hall)
            ไม่บังคับกรอก / หน้าตรวจสอบข้อมูลใช้ฟอร์มนี้แบบ disabled จึงเห็นหมายเหตุด้วย */}
        <div className="input_wrap note_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextArea
                {...field}
                disabled={isDisabledForm}
                placeholder={t("Additional note", { ns: "room" })}
                errors={errors.special_request?.message}
              />
            )}
            name="special_request"
            defaultValue=""
          />
        </div>
        <div className="button_layout">
          <div className="button1">
            <Buttons.BgStandard
              theme_white_purple
              label={t("Back", { ns: "common" })}
              onClick={onBack}
            />
          </div>
          <div className="button2">
            {!isDisabledForm && (
              <Buttons.BgStandard
                label={t("Next", { ns: "common" })}
                type="submit"
              />
            )}
          </div>
        </div>
      </form>
    </MusicPracticeRoomSelectDateFormStyled>
  );
};

// ห้องซ้อมจองได้ถึง 17.30 (เวลาจบ) ต้องตรงกับ PRACTICE_ROOM_LAST_END_TIME ฝั่ง API (draft)
const MUSIC_PRACTICE_ROOM_TIME_OPTIONS = [
  { value: "09.00", label: "09:00" },
  { value: "09.30", label: "09:30" },
  { value: "10.00", label: "10:00" },
  { value: "10.30", label: "10:30" },
  { value: "11.00", label: "11:00" },
  { value: "11.30", label: "11:30" },
  { value: "12.00", label: "12:00" },
  { value: "12.30", label: "12:30" },
  { value: "13.00", label: "13:00" },
  { value: "13.30", label: "13:30" },
  { value: "14.00", label: "14:00" },
  { value: "14.30", label: "14:30" },
  { value: "15.00", label: "15:00" },
  { value: "15.30", label: "15:30" },
  { value: "16.00", label: "16:00" },
  { value: "16.30", label: "16:30" },
  { value: "17.00", label: "17:00" },
  { value: "17.30", label: "17:30" },
];

// จองขั้นต่ำ 30 นาที ต้องตรงกับ PRACTICE_ROOM_MIN_BOOKING_MINUTES ฝั่ง API (draft)
const MIN_BOOKING_MINUTES = 30;
// เวลาเก็บเป็น "HH.MM" (เช่น "09.30") ใช้ parseFloat คำนวณช่วงห่างไม่ได้ (9.3 ไม่ใช่ 9.5) ต้องแปลงเป็นนาที
const TO_MINUTES = (time) => {
  const [hour, minute] = String(time).split(".").map(Number);
  return hour * 60 + (minute || 0);
};
const IS_MIN_DURATION = (startTime, endTime) =>
  TO_MINUTES(endTime) - TO_MINUTES(startTime) >= MIN_BOOKING_MINUTES;

const FIRST_TIME = MUSIC_PRACTICE_ROOM_TIME_OPTIONS[0].value;
const LAST_TIME =
  MUSIC_PRACTICE_ROOM_TIME_OPTIONS[MUSIC_PRACTICE_ROOM_TIME_OPTIONS.length - 1]
    .value;
// เวลาเริ่มเลือกได้ถึง 17.00 (ต้องเหลือเวลาจบอย่างน้อย 30 นาที ก่อน 17.30)
const MUSIC_PRACTICE_ROOM_START_TIME_OPTIONS =
  MUSIC_PRACTICE_ROOM_TIME_OPTIONS.filter((e) =>
    IS_MIN_DURATION(e.value, LAST_TIME),
  );
// เวลาจบเริ่มที่ 09.30 (ยังไม่เลือกเวลาเริ่มก็ไม่ให้เห็นเวลาจบที่จองไม่ได้)
const MUSIC_PRACTICE_ROOM_END_TIME_OPTIONS =
  MUSIC_PRACTICE_ROOM_TIME_OPTIONS.filter((e) =>
    IS_MIN_DURATION(FIRST_TIME, e.value),
  );

const TIME_DATA = [
  { label: "Time" },
  { label: "09.00\n09.30", value: { startTime: "09.00", endTime: "09.30" } },
  { label: "09.30\n10.00", value: { startTime: "09.30", endTime: "10.00" } },
  { label: "10.00\n10.30", value: { startTime: "10.00", endTime: "10.30" } },
  { label: "10.30\n11.00", value: { startTime: "10.30", endTime: "11.00" } },
  { label: "11.00\n11.30", value: { startTime: "11.00", endTime: "11.30" } },
  { label: "11.30\n12.00", value: { startTime: "11.30", endTime: "12.00" } },
  { label: "12.00\n12.30", value: { startTime: "12.00", endTime: "12.30" } },
  { label: "12.30\n13.00", value: { startTime: "12.30", endTime: "13.00" } },
  { label: "13.00\n13.30", value: { startTime: "13.00", endTime: "13.30" } },
  { label: "13.30\n14.00", value: { startTime: "13.30", endTime: "14.00" } },
  { label: "14.00\n14.30", value: { startTime: "14.00", endTime: "14.30" } },
  { label: "14.30\n15.00", value: { startTime: "14.30", endTime: "15.00" } },
  { label: "15.00\n15.30", value: { startTime: "15.00", endTime: "15.30" } },
  { label: "15.30\n16.00", value: { startTime: "15.30", endTime: "16.00" } },
  { label: "16.00\n16.30", value: { startTime: "16.00", endTime: "16.30" } },
  { label: "16.30\n17.00", value: { startTime: "16.30", endTime: "17.00" } },
  { label: "17.00\n17.30", value: { startTime: "17.00", endTime: "17.30" } },
];

MusicPracticeRoomSelectDateForm.propTypes = {};

export default MusicPracticeRoomSelectDateForm;
